import React, { useState } from 'react';
import { createCustomer, getCustomers, getCustomer, updateCustomer, deleteCustomer } from '../utils/api';
import ResponseDisplay from './ResponseDisplay';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const CustomerForm = () => {
  const [formData, setFormData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);

  const validateForm = (action) => {
    const newErrors = {};
    if (!formData.firstName && action === 'create') newErrors.firstName = 'First name is required';
    if (!formData.lastName && action === 'create') newErrors.lastName = 'Last name is required';
    if (!formData.email && action === 'create') newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email) && action === 'create') newErrors.email = 'Invalid email format';
    if (!formData.password && action === 'create') newErrors.password = 'Password is required';
    if (!formData.id && ['get', 'update', 'delete'].includes(action)) {
      newErrors.id = 'Customer ID is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const executeAction = async (action) => {
    if (!validateForm(action)) return;
    setLoading(true);
    setLoadingMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} customer, please wait... The backend may take up few seconds to wake up.`);

    try {
      let res;
      switch (action) {
        case 'create':
          res = await createCustomer(formData);
          break;
        case 'getAll':
          res = await getCustomers();
          break;
        case 'get':
          res = await getCustomer(formData.id);
          break;
        case 'update':
          res = await updateCustomer(formData.id, formData);
          break;
        case 'delete':
          await deleteCustomer(formData.id);
          res = { data: { message: 'Customer deleted successfully' } };
          break;
        default:
          throw new Error('Invalid action');
      }
      setResponse(res.data);
      setError(null);
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} successful!`);
    } catch (err) {
      setError(err.response?.data || { error: `Failed to ${action} customer` });
      setResponse(null);
      toast.error(`Failed to ${action} customer`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="max-w-4xl p-8 mx-auto mt-24 mb-12 transition-all duration-300 bg-white shadow-xl rounded-2xl">
      {loading && <LoadingSpinner message={loadingMessage} />}
      <h2 className="mb-6 text-3xl font-bold text-blue-600">Manage Customers</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {[
          { name: 'id', label: 'Customer ID', placeholder: 'Enter Customer ID (for Get/Update/Delete)', type: 'text' },
          { name: 'firstName', label: 'First Name', placeholder: 'Enter First Name', type: 'text' },
          { name: 'lastName', label: 'Last Name', placeholder: 'Enter Last Name', type: 'text' },
          { name: 'email', label: 'Email', placeholder: 'Enter Email', type: 'email' },
          { name: 'password', label: 'Password', placeholder: 'Enter Password', type: 'password' },
        ].map((field) => (
          <div key={field.name} className="relative">
            <label className="block mb-1 text-sm font-medium text-gray-700">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              placeholder={field.placeholder}
              value={formData[field.name]}
              onChange={handleChange}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors[field.name] ? 'border-red-500' : 'border-gray-300'}`}
              disabled={loading}
              aria-describedby={`${field.name}-error`}
            />
            {errors[field.name] && (
              <p id={`${field.name}-error`} className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-8">
        {[
          { action: 'create', label: 'Create', color: 'bg-blue-600 hover:bg-blue-700' },
          { action: 'getAll', label: 'Get All', color: 'bg-green-500 hover:bg-green-600' },
          { action: 'get', label: 'Get by ID', color: 'bg-yellow-500 hover:bg-yellow-600' },
          { action: 'update', label: 'Update', color: 'bg-orange-500 hover:bg-orange-600' },
          { action: 'delete', label: 'Delete', color: 'bg-red-500 hover:bg-red-600' },
        ].map(({ action, label, color }) => (
          <button
            key={action}
            onClick={() => executeAction(action)}
            disabled={loading}
            className={`${color} text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={label}
          >
            {loading ? 'Processing...' : label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <button
          onClick={() => setShowSampleData(!showSampleData)}
          className="flex items-center font-semibold text-blue-600 transition-all hover:text-blue-800"
          aria-expanded={showSampleData}
          aria-controls="sample-data"
        >
          {showSampleData ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
          Sample Data
        </button>
        {showSampleData && (
          <div id="sample-data" className="p-6 mt-4 bg-gray-50 rounded-xl animate-fade-in">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Sample Customer Data</h3>
            <pre className="p-4 overflow-auto font-mono text-sm text-gray-800 bg-white rounded-lg">
              {JSON.stringify(
                {
                  firstName: "John",
                  lastName: "Doe",
                  email: "john.doe@example.com",
                  password: "password123",
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
      <ResponseDisplay response={response} error={error} />
    </div>
  );
};

export default CustomerForm;